import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { DollarSign } from "lucide-react";
import "./CustomerPaidments.css";

const url = process.env.REACT_APP_BACKEND_URL;

const EditCustomerPayments = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  
  const [customers, setCustomers] = useState([]);
  const [collectors, setCollectors] = useState([]);
  
  const [formData, setFormData] = useState({
    customerId: "",
    houseNo: "",
    collectorId: "",
    amount: "",
    paymentMethod: "EVC",
    paymentStatus: "Paid",
    description: ""
  });

  // Fetch payment data
  useEffect(() => {
    const fetchPayment = async () => {
      try {
        console.log("📤 Fetching payment:", id);
        const res = await axios.get(`${url}/api/payment/${id}`);
        const payment = res.data.data;
        
        console.log("📥 Payment data:", payment);

        setFormData({
          customerId: payment.customer?._id || "",
          houseNo: payment.houseNo || "",
          collectorId: payment.collector?._id || "",
          amount: payment.amount?.toString() || "",
          paymentMethod: payment.paymentMethod || "EVC",
          paymentStatus: payment.paymentStatus || "Paid",
          description: payment.description || ""
        });

        setLoading(false);
      } catch (err) {
        console.error("❌ Failed to load payment:", err);
        toast.error(err.response?.data?.message || "Failed to load payment");
        navigate("/payments");
      }
    };
    
    if (id) {
      fetchPayment();
    }
  }, [id, navigate]);

  // Fetch customers and collectors
  useEffect(() => {
    const fetchOptions = async () => {
      try {
        console.log("📤 Fetching options...");
        const [custRes, colRes] = await Promise.all([
          axios.get(`${url}/api/customer`),
          axios.get(`${url}/api/collectors`),
        ]);
        
        setCustomers(custRes.data.data || []);
        setCollectors(colRes.data.data || []);
        
        console.log("📥 Options loaded:", {
          customers: custRes.data.data?.length,
          collectors: colRes.data.data?.length
        });
      } catch (err) {
        console.error("❌ Failed to load options:", err);
        toast.error("Failed to load form options");
      }
    };
    fetchOptions();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    // Validate required fields
    if (!formData.customerId || !formData.amount) {
      toast.error("Please fill all required fields");
      setSubmitting(false);
      return;
    }

    if (parseFloat(formData.amount) <= 0) {
      toast.error("Amount must be greater than 0");
      setSubmitting(false);
      return;
    }

    try {
      console.log("📤 Updating payment with data:", formData);
      
      const payload = {
        amount: parseFloat(formData.amount),
        paymentMethod: formData.paymentMethod,
        paymentStatus: formData.paymentStatus,
        description: formData.description
      };

      const res = await axios.put(`${url}/api/payment/${id}`, payload);
      
      console.log("📥 Update response:", res.data);
      toast.success("Payment updated successfully!");
      navigate("/payments");
    } catch (err) {
      console.error("❌ Update failed:", err);
      console.error("Error response:", err.response?.data);
      toast.error(err.response?.data?.message || "Update failed");
    } finally {
      setSubmitting(false);
    }
  };

  // Get selected customer details for display
  const selectedCustomer = customers.find(c => c._id === formData.customerId);
  const selectedCollector = collectors.find(c => c._id === (selectedCustomer?.collector?._id || formData.collectorId));

  if (loading) {
    return (
      <div className="payments-container">
        <div className="loading">Loading payment data...</div>
      </div>
    );
  }

  return (
    <div className="payments-container">
      <div className="header-section">
        <h1 className="page-title">
          <DollarSign size={24} className="title-icon" /> Edit Payment
        </h1>
        <button 
          onClick={() => navigate("/payments")} 
          className="export-btn"
        >
          Back to List
        </button>
      </div>

      <div className="filters-section" style={{ display: 'block' }}>
        <form onSubmit={handleSubmit}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '20px' }}>
            {/* Customer (Read Only) */}
            <div className="form-group">
              <label>Customer</label>
              <input
                type="text"
                value={selectedCustomer?.customerName || "N/A"}
                className="filter-input"
                readOnly
                disabled
              />
            </div>

            {/* House Number (Read Only) */}
            <div className="form-group">
              <label>House Number</label>
              <input
                type="text"
                value={selectedCustomer?.houseNo || formData.houseNo}
                className="filter-input"
                readOnly
                disabled
              />
            </div>

            {/* Collector (Read Only) */}
            <div className="form-group">
              <label>Collector</label>
              <input
                type="text"
                value={selectedCollector ? `${selectedCollector.fname} ${selectedCollector.lname}` : "N/A"}
                className="filter-input"
                readOnly
                disabled
              />
            </div>

            {/* Amount */}
            <div className="form-group">
              <label>Amount ($) *</label>
              <input
                type="number"
                name="amount"
                value={formData.amount}
                onChange={handleChange}
                placeholder="Enter amount"
                min="0.01"
                step="0.01"
                required
                className="filter-input"
              />
            </div>

            {/* Payment Method */}
            <div className="form-group">
              <label>Payment Method</label>
              <select
                name="paymentMethod"
                value={formData.paymentMethod}
                onChange={handleChange}
                className="filter-select"
              >
                <option value="Cash">Cash</option>
                <option value="Mobile Money">Mobile Money</option>
                <option value="Card">Card</option>
                <option value="EVC">EVC</option>
                <option value="Other">Other</option>
              </select>
            </div>

            {/* Payment Status */}
            <div className="form-group">
              <label>Payment Status</label>
              <select
                name="paymentStatus"
                value={formData.paymentStatus}
                onChange={handleChange}
                className="filter-select"
              >
                <option value="Paid">Paid</option>
                <option value="Pending">Pending</option>
              </select>
            </div>

            {/* Description - Full Width */}
            <div className="form-group" style={{ gridColumn: 'span 2' }}>
              <label>Description (Optional)</label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                placeholder="Enter any notes"
                rows="3"
                className="filter-input"
                style={{ resize: 'vertical' }}
              />
            </div>
          </div>

          <div className="form-actions" style={{ marginTop: '30px', display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
            <button
              type="button"
              className="summary-btn"
              onClick={() => navigate("/payments")}
              style={{ backgroundColor: '#f44336' }}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="export-btn"
              disabled={submitting}
            >
              {submitting ? "Updating..." : "Update Payment"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditCustomerPayments;